import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, PenLine, Upload, CheckCircle2, Eraser, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/hipaa/sign`;
const errMsg = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";

function TemplateView({ token, contentType }) {
  const src = `${API}/${token}/template`;
  return contentType === "application/pdf" ? (
    <div>
      <iframe src={src} title="HIPAA agreement" className="w-full h-[60vh] rounded-lg border border-border bg-white" data-testid="hipaa-sign-template" />
      <a href={src} target="_blank" rel="noreferrer" className="text-xs text-primary underline mt-1 inline-block" data-testid="hipaa-sign-open-template">Open / download the document</a>
    </div>
  ) : (
    <img src={src} alt="HIPAA agreement" className="w-full rounded-lg border border-border bg-white" data-testid="hipaa-sign-template" />
  );
}

function ESignForm({ token, defaultName, onDone }) {
  const sigRef = useRef(null);
  const wrapRef = useRef(null);
  useEffect(() => {
    const fit = () => {
      const c = sigRef.current?.getCanvas();
      const w = wrapRef.current;
      if (!c || !w) return;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      c.width = w.offsetWidth * ratio;
      c.height = 160 * ratio;
      c.getContext("2d").scale(ratio, ratio);
      sigRef.current.clear();
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);
  const [name, setName] = useState(defaultName || "");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!name.trim()) return setError("Please type your full name.");
    if (!agreed) return setError("Please check the agreement box.");
    if (!sigRef.current || sigRef.current.isEmpty()) return setError("Please draw your signature.");
    setBusy(true);
    try {
      await axios.post(`${API}/${token}/esign`, { name: name.trim(), agreed, signature: sigRef.current.getCanvas().toDataURL("image/png") });
      onDone("esign");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="hipaa-esign-form">
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full legal name" data-testid="hipaa-esign-name" />
      <div ref={wrapRef} className="rounded-lg border-2 border-dashed border-border bg-white relative">
        <SignatureCanvas ref={sigRef} penColor="#111" canvasProps={{ className: "w-full h-40 rounded-lg touch-none", "data-testid": "hipaa-esign-pad" }} />
        <button type="button" onClick={() => sigRef.current?.clear()} className="absolute top-2 right-2 text-xs text-muted-foreground inline-flex items-center gap-1" data-testid="hipaa-esign-clear">
          <Eraser className="h-3 w-3" /> Clear
        </button>
      </div>
      <label className="flex items-start gap-2 text-sm cursor-pointer">
        <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} data-testid="hipaa-esign-agree" className="mt-0.5" />
        <span>I have read and agree to the HIPAA agreement above, and I understand my electronic signature is legally binding.</span>
      </label>
      {error && <p className="text-sm text-destructive" data-testid="hipaa-sign-error">{error}</p>}
      <Button onClick={submit} disabled={busy} className="w-full h-11 rounded-full" data-testid="hipaa-esign-submit">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PenLine className="h-4 w-4 mr-2" />} Sign agreement
      </Button>
    </div>
  );
}

function UploadForm({ token, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!file) return setError("Choose a PDF or photo of the signed document.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      await axios.post(`${API}/${token}/upload`, fd);
      onDone("upload");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="hipaa-upload-form">
      <p className="text-sm text-muted-foreground">Print the document, sign it, then take a clear photo or scan and upload it here.</p>
      <Input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" capture="environment" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="hipaa-upload-file" />
      {error && <p className="text-sm text-destructive" data-testid="hipaa-sign-error">{error}</p>}
      <Button onClick={submit} disabled={busy} className="w-full h-11 rounded-full" data-testid="hipaa-upload-submit">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Upload signed copy
      </Button>
    </div>
  );
}

export default function HipaaSign() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("esign");

  useEffect(() => {
    axios.get(`${API}/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(errMsg(e)));
  }, [token]);

  const done = (method) => setInfo((i) => ({ ...i, status: "signed", method, signed_at: new Date().toISOString() }));

  return (
    <div className="min-h-screen bg-secondary/40 px-4 py-8" data-testid="hipaa-sign-page">
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-[11px] uppercase tracking-[0.18em] font-semibold">
          <ShieldCheck className="h-3 w-3" /> {info?.company || "HIPAA"}
        </div>
        <h1 className="text-3xl font-display font-bold tracking-tight">HIPAA Agreement</h1>
        {error && <Card className="p-5 text-destructive" data-testid="hipaa-sign-link-error">{error}</Card>}
        {info?.status === "signed" && (
          <Card className="p-6 text-center space-y-2" data-testid="hipaa-sign-done">
            <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
            <div className="text-lg font-semibold">Thank you{info.driver_name ? `, ${info.driver_name}` : ""}!</div>
            <p className="text-sm text-muted-foreground">Your signed HIPAA agreement was received. A copy has been emailed to you.</p>
          </Card>
        )}
        {info && info.status !== "signed" && (
          <>
            <p className="text-muted-foreground text-sm">Hi {info.driver_name || "there"} — please review the document below and return it signed.</p>
            <TemplateView token={token} contentType={info.template_content_type} />
            <Card className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-2 p-1 rounded-full bg-secondary" data-testid="hipaa-sign-mode">
                {[["esign", "Sign online", PenLine], ["upload", "Upload signed copy", Upload]].map(([k, label, Icon]) => (
                  <button key={k} type="button" onClick={() => setMode(k)} data-testid={`hipaa-sign-mode-${k}`}
                    className={`h-9 rounded-full text-sm font-medium inline-flex items-center justify-center gap-1.5 transition-colors ${mode === k ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </button>
                ))}
              </div>
              {mode === "esign" ? <ESignForm token={token} defaultName={info.driver_name} onDone={done} /> : <UploadForm token={token} onDone={done} />}
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
